/* eslint-disable no-restricted-imports */
import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from '@material-ui/icons';

import PropTypes from 'prop-types';
import { clientService } from 'services';

import { useStyles } from './styles';

const ESTADOS_BRASIL = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

export default function CreateClientDialog({ open, onClose, onClientCreated }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    NOME: '',
    APELIDO: '',
    CGC: '',
    EMAIL: '',
    PESSOA: 'F',
    ENDERECO: '',
    numEnd: '',
    COMPLEMENTO: '',
    BAIRRO: '',
    CIDADE: '',
    ESTADO: '',
    CEP: '',
    DDD1: '',
    FONE: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCepBlur = async () => {
    const cep = formData.CEP.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const cepPromise = require('cep-promise');
        const address = await cepPromise(cep);

        setFormData((prev) => ({
          ...prev,
          ENDERECO: address.street || prev.ENDERECO,
          BAIRRO: address.neighborhood || prev.BAIRRO,
          CIDADE: address.city || prev.CIDADE,
          ESTADO: address.state || prev.ESTADO,
        }));

        toast.success('CEP encontrado!');
      } catch (error) {
        toast.warning('CEP não encontrado. Preencha os dados manualmente.');
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.NOME) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!formData.CGC) {
      toast.error('CPF/CNPJ é obrigatório');
      return;
    }

    if (!formData.EMAIL) {
      toast.error('E-mail é obrigatório');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        TIPO: 'C',
        ATIVO: 1,
        INSCRICAO: 'ISENTO',
      };

      const response = await clientService.createClient(payload);
      toast.success('Cliente cadastrado com sucesso!');

      if (onClientCreated) {
        onClientCreated(response.data);
      }

      setFormData({
        NOME: '',
        APELIDO: '',
        CGC: '',
        EMAIL: '',
        PESSOA: 'F',
        ENDERECO: '',
        numEnd: '',
        COMPLEMENTO: '',
        BAIRRO: '',
        CIDADE: '',
        ESTADO: '',
        CEP: '',
        DDD1: '',
        FONE: '',
      });

      onClose();
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar cliente. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center'>
            <PersonAddIcon className={classes.titleIcon} />
            <Typography variant='h6'>Cadastrar Novo Cliente</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* Tipo de Pessoa */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label='Tipo de Pessoa'
              value={formData.PESSOA}
              onChange={(e) => handleChange('PESSOA', e.target.value)}
              variant='outlined'
              required
            >
              <MenuItem value='F'>Pessoa Física</MenuItem>
              <MenuItem value='J'>Pessoa Jurídica</MenuItem>
            </TextField>
          </Grid>

          {/* Nome Completo */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label={formData.PESSOA === 'F' ? 'Nome Completo' : 'Razão Social'}
              value={formData.NOME}
              onChange={(e) => handleChange('NOME', e.target.value)}
              variant='outlined'
              required
            />
          </Grid>

          {/* Apelido */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={formData.PESSOA === 'F' ? 'Apelido' : 'Nome Fantasia'}
              value={formData.APELIDO}
              onChange={(e) => handleChange('APELIDO', e.target.value)}
              variant='outlined'
            />
          </Grid>

          {/* CPF/CNPJ */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={formData.PESSOA === 'F' ? 'CPF' : 'CNPJ'}
              value={formData.CGC}
              onChange={(e) => handleChange('CGC', e.target.value.replace(/\D/g, ''))}
              variant='outlined'
              required
              inputProps={{ maxLength: formData.PESSOA === 'F' ? 11 : 14 }}
              placeholder={formData.PESSOA === 'F' ? '000.000.000-00' : '00.000.000/0000-00'}
            />
          </Grid>

          {/* E-mail */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='E-mail'
              type='email'
              value={formData.EMAIL}
              onChange={(e) => handleChange('EMAIL', e.target.value)}
              variant='outlined'
              required
            />
          </Grid>

          {/* CEP */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='CEP'
              value={formData.CEP}
              onChange={(e) => handleChange('CEP', e.target.value.replace(/\D/g, ''))}
              onBlur={handleCepBlur}
              variant='outlined'
              inputProps={{ maxLength: 8 }}
              placeholder='00000-000'
            />
          </Grid>

          {/* Endereço */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Endereço'
              value={formData.ENDERECO}
              onChange={(e) => handleChange('ENDERECO', e.target.value)}
              variant='outlined'
            />
          </Grid>

          {/* Número */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label='Número'
              value={formData.numEnd}
              onChange={(e) => handleChange('numEnd', e.target.value)}
              variant='outlined'
            />
          </Grid>

          {/* Complemento */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Complemento'
              value={formData.COMPLEMENTO}
              onChange={(e) => handleChange('COMPLEMENTO', e.target.value)}
              variant='outlined'
            />
          </Grid>

          {/* Bairro */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Bairro'
              value={formData.BAIRRO}
              onChange={(e) => handleChange('BAIRRO', e.target.value)}
              variant='outlined'
            />
          </Grid>

          {/* Cidade */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Cidade'
              value={formData.CIDADE}
              onChange={(e) => handleChange('CIDADE', e.target.value)}
              variant='outlined'
            />
          </Grid>

          {/* Estado */}
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label='Estado'
              value={formData.ESTADO}
              onChange={(e) => handleChange('ESTADO', e.target.value)}
              variant='outlined'
            >
              {ESTADOS_BRASIL.map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estado}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* DDD */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label='DDD'
              value={formData.DDD1}
              onChange={(e) => handleChange('DDD1', e.target.value.replace(/\D/g, ''))}
              variant='outlined'
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          {/* Telefone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Telefone'
              value={formData.FONE}
              onChange={(e) => handleChange('FONE', e.target.value.replace(/\D/g, ''))}
              variant='outlined'
              inputProps={{ maxLength: 9 }}
              placeholder='000000000'
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} disabled={loading} color='default'>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant='contained'
          color='primary'
          className={classes.submitButton}
        >
          {loading ? 'Cadastrando...' : 'Cadastrar Cliente'}
        </Button>
      </DialogActions>

      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </Dialog>
  );
}

CreateClientDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onClientCreated: PropTypes.func,
};
